import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import printProvider from '@/services/printProvider';
import { toast } from 'sonner';
import API_URL from '@/utils/api';

const API = API_URL;

/**
 * Worker invisible que gestiona la cola de impresión en el nodo local (Caja/POS).
 * Se encarga de:
 * 1. Sondear pedidos con status 'pending' de impresión (cocina o cliente).
 * 2. Imprimir usando QZ Tray.
 * 3. Actualizar el estado en el backend a 'printed' o 'failed'.
 */
export default function PrintQueueWorker({ branchId, onStatusChange }) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [lastError, setLastError] = useState(null);

    // Ref para evitar ejecuciones concurrentes del ciclo de polling
    const isPollingRef = useRef(false);
    const branchIdRef = useRef(branchId);

    // Mantener ref actualizada
    useEffect(() => {
        branchIdRef.current = branchId;
    }, [branchId]);

    // Función principal de procesamiento
    const processQueue = useCallback(async () => {
        // DISABLE POLLING IN DEMO MODE TO PREVENT ERRORS
        if (sessionStorage.getItem("demo_mode") === "true") {
            return;
        }

        if (isPollingRef.current) return;
        isPollingRef.current = true;
        setIsProcessing(true);

        try {
            // 1. Reclamar pedidos atómicamente
            const currentBranch = branchIdRef.current;
            if (!currentBranch || currentBranch === 'all') {
                isPollingRef.current = false;
                setIsProcessing(false);
                return;
            }

            // Identificar dispositivo único (simple random si no existe)
            let deviceId = localStorage.getItem('pos_device_id');
            if (!deviceId) {
                deviceId = `dev_${Math.random().toString(36).substr(2, 9)}`;
                localStorage.setItem('pos_device_id', deviceId);
            }

            const response = await axios.post(`${API}/admin/orders/claim-pending-prints`,
                { branch_id: currentBranch, device_id: deviceId },
                { withCredentials: true }
            );

            const ordersToPrint = response.data; // Ya vienen marcados como "printing" y asignados a mi
            setPendingCount(ordersToPrint.length);

            if (onStatusChange) {
                onStatusChange({ count: ordersToPrint.length, processing: true });
            }

            // 2. Procesar uno a uno
            for (const task of ordersToPrint) {
                const { order, type } = task; // type: 'kitchen' | 'customer'

                console.log(`[PrintWorker] Procesando pedido ${order.order_id} tipo ${type}`);
                console.log(`[PRINT] type=${type} order_id=${order.order_id}`);

                try {
                    // A. Imprimir
                    // Modificar PrintService para aceptar "forceType" si es necesario, 
                    // pero generate_customer_ticket vs generate_kitchen_ticket ya lo maneja el backend
                    // El backend nos da el TEZTO o nosotros lo generamos?
                    // El frontend actual genera el texto en JS? O recibe texto?
                    // Revisando PrintService.js: recibe objeto order y genera ESC/POS o recibe texto.
                    // El backend YA TIENE endpoints de generación de texto?
                    // En AdminPOS/Orders.py vimos que el backend genera texto en /pos endpoint, pero aqui estamos
                    // en un worker desconectado.
                    // OPCION MEJOR: Usar PrintService del frontend que ya tiene templates (ReceiptTemplate, KitchenTemplate).
                    // PERO PrintService.js actual (visto en logs) tiene metodos `printOrder`.

                    let success = false;

                    if (type === 'kitchen') {
                        const result = await printProvider.printKitchenTicket(order);
                        success = result.success;
                    } else {
                        const result = await printProvider.printCustomerTicket(order);
                        success = result.success;
                    }

                    // B. Actualizar resultado
                    if (success) {
                        await updatePrintStatus(order.order_id, type, "printed");
                        console.log(`[PrintWorker] Impreso ${type} para ${order.order_id}`);
                    } else {
                        console.warn(`[PrintWorker] Fallo impresión ${type} para ${order.order_id}`);
                        // Dejar en pending (poll siguiente) o failed
                        // Si es error de conexión, mejor dejar pending.
                        // Si es QZ no conectado, pending.
                        await updatePrintStatus(order.order_id, type, "pending"); // Reintentar luego
                    }

                } catch (err) {
                    console.error(`[PrintWorker] Error procesando ${order.order_id}:`, err);
                    setLastError(err.message);
                    // Reintentar luego
                }
            }

        } catch (error) {
            console.error("[PrintWorker] Error polling queue:", error);
        } finally {
            isPollingRef.current = false;
            setIsProcessing(false);
            if (onStatusChange) {
                onStatusChange({ count: 0, processing: false }); // Reset visual state mostly
            }
        }
    }, []);

    // Endpoint helper
    const updatePrintStatus = async (orderId, type, status) => {
        try {
            await axios.put(
                `${API}/orders/${orderId}/print-status`,
                { status, type },
                { withCredentials: true }
            );
        } catch (e) {
            console.error("Failed to update status", e);
        }
    };

    // Ciclo de vida: Polling cada 1s (Imprensión instantánea)
    useEffect(() => {
        const interval = setInterval(processQueue, 1000);
        // Ejecutar inmediatamente al montar
        processQueue();
        return () => clearInterval(interval);
    }, [processQueue]);

    return null; // Componente invisible (lógica pura)
}
